import { FunctionComponent, useEffect } from 'react';
import SearchBar from '../../../ui/inputs/SearchBar';
import { SubcategoryComboBox } from './SubCategoryComboBox';
import {
  FundFailureSummary,
  PieStatusKey,
  RegulationId,
} from '../types/dashboard.types';
import { IoIosCheckmark } from 'react-icons/io';
import { Checkbox } from '@headlessui/react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import ExportButton from '../../../feedback/ExportButton';
import { useDispatch } from 'react-redux';
import { addComponentToPdfExport } from '../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../types/redux/pdfExports/FileExportsStore';
import { percentageToTwoDecimalPlaces } from '../../../../utilities/numberFormatters';
import { tailwindColors } from '../../../../../tailwind.config';

const CHECKBOX_STATUS: PieStatusKey[] = ['Fail', 'Alert', 'Pass'];

const getStatusCheckboxClasses = (status: PieStatusKey, isChecked: boolean) => {
  const baseClasses =
    'group flex cursor-pointer items-center gap-2 rounded p-1.5 transition-all duration-200';
  const checkboxClasses =
    'flex size-5 flex-shrink-0 items-center justify-center rounded border-2 bg-white transition-all duration-200';
  const textClasses =
    'select-none text-sm font-medium transition-colors duration-200';

  switch (status) {
    case 'Fail':
      return {
        container: `${baseClasses} hover:border-red-300 hover:bg-red-50`,
        checkbox: `${checkboxClasses} group-hover:border-gray-400`,
        text: `${textClasses}`,
        checkboxStyle: {
          borderColor: tailwindColors.fail,
          backgroundColor: isChecked ? tailwindColors.fail : 'white',
        },
        textStyle: {
          color: tailwindColors.fail,
        },
      };
    case 'Alert':
      return {
        container: `${baseClasses} hover:border-orange-300 hover:bg-orange-50`,
        checkbox: `${checkboxClasses} group-hover:border-gray-400`,
        text: `${textClasses}`,
        checkboxStyle: {
          borderColor: tailwindColors.alert,
          backgroundColor: isChecked ? tailwindColors.alert : 'white',
        },
        textStyle: {
          color: tailwindColors.alert,
        },
      };
    case 'Pass':
      return {
        container: `${baseClasses} hover:border-green-300 hover:bg-green-50`,
        checkbox: `${checkboxClasses} group-hover:border-gray-400`,
        text: `${textClasses}`,
        checkboxStyle: {
          borderColor: tailwindColors.pass,
          backgroundColor: isChecked ? tailwindColors.pass : 'white',
        },
        textStyle: {
          color: tailwindColors.pass,
        },
      };
    default:
      return {
        container: `${baseClasses} hover:border-blue-300 hover:bg-blue-50`,
        checkbox: `${checkboxClasses} border-gray-300 group-hover:border-gray-400 ${isChecked ? 'border-blue-500 bg-blue-500' : ''}`,
        text: `${textClasses} text-gray-700 ${isChecked ? 'text-blue-700' : ''}`,
        checkboxStyle: {},
        textStyle: {},
      };
  }
};

// Export fields (include backend percent keys, values in [0,1])
const fields = [
  'fund_id',
  'fund_name',
  'fund_name_full',
  'regulation',
  'fail_count',
  'alert_count',
  'pass_count',
  'total_runs',
  'fail_rate_percent',
  'alert_rate_percent',
  'pass_rate_percent',
];

interface ChartToolbarProps {
  fundFilters: string[];
  setFundFilters: React.Dispatch<React.SetStateAction<string[]>>;
  subCategoryFilters: string[];
  setSubCategoryFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setSearchTerm: (term: string) => void;
  allFunds: string[];
  statusFilters: PieStatusKey[];
  toggleStatus: (status: PieStatusKey) => void;
  subCategoriesStatus: string[];
  regulation: RegulationId;
  data: FundFailureSummary[];
}

const ChartToolbar: FunctionComponent<ChartToolbarProps> = ({
  fundFilters,
  setFundFilters,
  subCategoryFilters,
  setSubCategoryFilters,
  subCategoriesStatus,
  setSearchTerm,
  allFunds,
  statusFilters,
  toggleStatus,
  regulation,
  data,
}) => {
  const dispatch = useDispatch();

  // Add PDF export handler
  const tablePdfExportHandler = async () => {
    const renderColumns = [
      { header: 'Fund Id', dataKey: 'fund_name' },
      { header: 'Fund Name', dataKey: 'fund_name_full' },
      { header: 'Regulation', dataKey: 'regulation' },
      { header: 'Fail Count', dataKey: 'fail_count' },
      { header: 'Alert Count', dataKey: 'alert_count' },
      { header: 'Pass Count', dataKey: 'pass_count' },
      { header: 'Total Runs', dataKey: 'total_runs' },
      { header: 'Fail Rate', dataKey: 'fail_rate_percent' },
      { header: 'Alert Rate', dataKey: 'alert_rate_percent' },
      { header: 'Pass Rate', dataKey: 'pass_rate_percent' },
    ];

    // Format percent keys for PDF body for readability
    const body = data.map((row) => ({
      ...row,
      fail_rate_percent: percentageToTwoDecimalPlaces(
        Number(row.fail_rate_percent) || 0,
        1,
      ),
      alert_rate_percent: percentageToTwoDecimalPlaces(
        Number(row.alert_rate_percent) || 0,
        1,
      ),
      pass_rate_percent: percentageToTwoDecimalPlaces(
        Number(row.pass_rate_percent) || 0,
        1,
      ),
    }));

    return {
      startY: 50,
      columns: renderColumns,
      body,
    };
  };

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: 'dashboard_chart',
        handler: tablePdfExportHandler,
        type: PdfComponentType.TABLE,
        title: 'Dashboard Chart',
        dontMoveToNewPage: true,
        groupOrder: 2,
        groupName: 'compliance_dashboard',
      }),
    );
  }, [data]);

  return (
    <header className="mb-2 flex w-full flex-wrap items-center justify-center gap-2 md:justify-start">
      {/* Controls */}
      <div className="flex w-full flex-wrap items-center justify-center gap-2 md:justify-end">
        <SearchBar
          searchHandler={setSearchTerm}
          className="min-w-[150px] flex-1 py-0.5 focus:ring focus:ring-blue-500 md:w-auto"
        />
        <SubcategoryComboBox
          data={allFunds}
          selected={fundFilters}
          setSelected={setFundFilters}
          placeholder="Filter Funds"
          className="min-w-[160px] py-0.5 text-sm"
        />
        <SubcategoryComboBox
          data={subCategoriesStatus}
          selected={subCategoryFilters}
          setSelected={setSubCategoryFilters}
          placeholder="Filter Sub-Categories"
          className="min-w-[180px] py-0.5 text-sm"
        />
        <div className="flex flex-wrap items-center gap-2">
          {CHECKBOX_STATUS.map((status) => {
            const isChecked = statusFilters.includes(status);
            const classes = getStatusCheckboxClasses(status, isChecked);

            return (
              <Checkbox
                key={status}
                checked={isChecked}
                onChange={() => toggleStatus(status)}
                className={classes.container}
              >
                <div className={classes.checkbox} style={classes.checkboxStyle}>
                  <IoIosCheckmark
                    className={`size-6 text-white transition-all duration-200 ${isChecked ? 'visible' : 'invisible'}`}
                  />
                </div>
                <span className={classes.text} style={classes.textStyle}>
                  {status}
                </span>
              </Checkbox>
            );
          })}
        </div>

        <Link
          className="flex items-center gap-1 rounded border border-blue-100 p-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
          to={`/${regulation.id}/overview`}
          title="Go to Regulation Overview"
        >
          <FaExternalLinkAlt className="h-2.5 w-2.5" />
          Overview
        </Link>

        <ExportButton
          exportData={data}
          fields={fields}
          allowPdfExport={true}
          pdfIdentifier={'dashboard'}
        />
      </div>
    </header>
  );
};

export default ChartToolbar;
